import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectItem,
  SortDescriptor,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import {
  ExternalReceiverProfile,
  getReceiverCommitments,
  getReceiverShipmentsPage,
  ReceiverCommitmentNote,
  ReceiverCommitmentRow,
  ReceiverShipmentRow,
} from '../../../../common/src/api/external-profiles';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import {
  Alert,
  EmptyState,
  KpiCard,
  LoadingState,
  PageHeader,
  PageLayout,
} from '../../../../common/src/components/ui';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import {
  getSeasonCutoffIndex,
  getSeasonWeeksForSpecies,
} from '../../../../common/src/utils/seasonWeeks';
import { getWeekKey } from '../../../../common/src/utils/functions';

interface ReceiverCommitmentsPageProps {
  profile: ExternalReceiverProfile;
  view: 'summary' | 'detail';
}

type FulfillmentMode = 'toDate' | 'season';
type ShipDateMode = 'etd' | 'eta';
type ChartViewMode = 'chart' | 'table';
type DetailViewMode = 'list' | 'matrix';
type WeekValueMode = 'weekly' | 'cumulative';

interface ChartDatum {
  label: string;
  value: number;
  committed?: number;
  shipped?: number;
}

const getLocale = (language?: string) => {
  if (language?.startsWith('es')) return 'es-CL';
  if (language?.startsWith('en')) return 'en-US';
  return 'zh-CN';
};

const formatNumber = (
  value: number,
  locale: string,
  maximumFractionDigits = 0
) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits }).format(
    Number(value) || 0
  );

const getShipmentWeek = (
  shipment: ReceiverShipmentRow,
  shipDateMode: ShipDateMode
) => {
  const date = shipDateMode === 'etd' ? shipment.ship_etd : shipment.ship_eta;
  const key = date ? getWeekKey(date) : null;
  const match = key ? String(key).match(/W(\d{1,2})$/) : null;
  return match ? Number(match[1]) : null;
};

const sortSeasonWeekRows = (
  rows: ChartDatum[],
  orderedWeeks: number[]
): ChartDatum[] =>
  [...rows].sort((a, b) => {
    const aWeek = Number(a.label.replace(/\D/g, ''));
    const bWeek = Number(b.label.replace(/\D/g, ''));
    const aIndex = orderedWeeks.indexOf(aWeek);
    const bIndex = orderedWeeks.indexOf(bWeek);
    if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
    return aWeek - bWeek;
  });

const getSortedRows = (rows: ChartDatum[], sortDescriptor: SortDescriptor) => {
  const column = String(sortDescriptor.column || 'value') as keyof ChartDatum;
  const direction = sortDescriptor.direction === 'ascending' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aValue = a[column];
    const bValue = b[column];
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction;
    }
    return String(aValue ?? '').localeCompare(String(bValue ?? '')) * direction;
  });
};

const SegmentedToggle = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  ariaLabel: string;
}) => (
  <Tabs
    size='sm'
    aria-label={ariaLabel}
    selectedKey={value}
    onSelectionChange={(key) => onChange(key as T)}
  >
    {options.map((option) => (
      <Tab key={option.value} title={option.label} />
    ))}
  </Tabs>
);

const SummaryChartCard = ({
  title,
  rows,
  locale,
  mode,
  onModeChange,
  chartLabel,
  tableLabel,
  weeklyLabel,
  cumulativeLabel,
  weekValueMode,
  onWeekValueModeChange,
  nameLabel,
  committedLabel,
  shippedLabel,
  emptyLabel,
}: {
  title: string;
  rows: ChartDatum[];
  locale: string;
  mode: ChartViewMode;
  onModeChange: (mode: ChartViewMode) => void;
  chartLabel: string;
  tableLabel: string;
  weeklyLabel?: string;
  cumulativeLabel?: string;
  weekValueMode?: WeekValueMode;
  onWeekValueModeChange?: (mode: WeekValueMode) => void;
  nameLabel: string;
  committedLabel: string;
  shippedLabel: string;
  emptyLabel: string;
}) => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'committed',
    direction: 'descending',
  });
  const sortedRows = useMemo(
    () => getSortedRows(rows, sortDescriptor),
    [rows, sortDescriptor]
  );
  const maxValue = Math.max(
    1,
    ...rows.flatMap((row) => [row.committed || 0, row.shipped || 0])
  );

  return (
    <Card shadow='sm'>
      <CardBody className='p-6'>
      <div className='flex items-center justify-between gap-3 mb-4'>
        <h2 className='text-base font-semibold text-foreground'>{title}</h2>
        <div className='flex flex-wrap items-center justify-end gap-2'>
          {weekValueMode && onWeekValueModeChange ? (
            <SegmentedToggle
              value={weekValueMode}
              onChange={onWeekValueModeChange}
              ariaLabel={`${weeklyLabel} / ${cumulativeLabel}`}
              options={[
                { value: 'weekly', label: weeklyLabel || '' },
                { value: 'cumulative', label: cumulativeLabel || '' },
              ]}
            />
          ) : null}
          <SegmentedToggle
            value={mode}
            onChange={onModeChange}
            ariaLabel={`${chartLabel} / ${tableLabel}`}
            options={[
              { value: 'chart', label: chartLabel },
              { value: 'table', label: tableLabel },
            ]}
          />
        </div>
      </div>

      {mode === 'table' ? (
        <Table
          removeWrapper
          aria-label={title}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        >
          <TableHeader>
            <TableColumn key='label' allowsSorting>
              {nameLabel}
            </TableColumn>
            <TableColumn key='committed' allowsSorting>
              {committedLabel}
            </TableColumn>
            <TableColumn key='shipped' allowsSorting>
              {shippedLabel}
            </TableColumn>
          </TableHeader>
          <TableBody emptyContent={emptyLabel} items={sortedRows}>
            {(row) => (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell>
                  {formatNumber(row.committed || 0, locale)}
                </TableCell>
                <TableCell>{formatNumber(row.shipped || 0, locale)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : rows.length === 0 ? (
        <EmptyState title={emptyLabel} />
      ) : (
        <div className='space-y-4'>
          <div className='flex items-center gap-4 text-xs text-default-500'>
            <span className='flex items-center gap-2'>
              <span className='h-2.5 w-2.5 rounded-sm bg-brand-700' />
              {committedLabel}
            </span>
            <span className='flex items-center gap-2'>
              <span className='h-2.5 w-2.5 rounded-sm bg-emerald-500' />
              {shippedLabel}
            </span>
          </div>
          <div className='h-64 flex items-end gap-4 overflow-x-auto pb-2'>
            {rows.map((row) => (
              <div
                key={row.label}
                className='flex min-w-[72px] flex-1 flex-col items-center gap-2'
              >
                <div className='h-48 w-full flex items-end justify-center gap-1.5 border-b border-default-200 px-1'>
                  <div
                    className='w-5 rounded-t bg-brand-700'
                    style={{
                      height: row.committed
                        ? `${Math.max(((row.committed || 0) / maxValue) * 100, 3)}%`
                        : 0,
                    }}
                    title={`${row.label} ${committedLabel}: ${formatNumber(
                      row.committed || 0,
                      locale
                    )}`}
                  />
                  <div
                    className='w-5 rounded-t bg-emerald-500'
                    style={{
                      height: row.shipped
                        ? `${Math.max(((row.shipped || 0) / maxValue) * 100, 3)}%`
                        : 0,
                    }}
                    title={`${row.label} ${shippedLabel}: ${formatNumber(
                      row.shipped || 0,
                      locale
                    )}`}
                  />
                </div>
                <p className='text-xs text-default-500 text-center line-clamp-2'>
                  {row.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      </CardBody>
    </Card>
  );
};

const ReceiverCommitmentsPage = ({
  profile,
  view,
}: ReceiverCommitmentsPageProps) => {
  const { t, currentLanguage } = useTranslation('receiver');
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const locale = useMemo(() => getLocale(currentLanguage), [currentLanguage]);
  const [rows, setRows] = useState<ReceiverCommitmentRow[]>([]);
  // Notas que las exportadoras dejan en su programa comercial.
  const [notes, setNotes] = useState<ReceiverCommitmentNote[]>([]);
  const [shipmentRows, setShipmentRows] = useState<ReceiverShipmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [selectedExporter, setSelectedExporter] = useState('all');
  const [detailViewMode, setDetailViewMode] =
    useState<DetailViewMode>('matrix');
  const [fulfillmentMode, setFulfillmentMode] =
    useState<FulfillmentMode>('toDate');
  const [shipDateMode, setShipDateMode] = useState<ShipDateMode>('etd');
  const [weekValueMode, setWeekValueMode] = useState<WeekValueMode>('weekly');
  const [chartModes, setChartModes] = useState<
    Record<'week' | 'exporter', ChartViewMode>
  >({
    week: 'chart',
    exporter: 'chart',
  });

  const setChartMode = (key: keyof typeof chartModes, mode: ChartViewMode) => {
    setChartModes((current) => ({ ...current, [key]: mode }));
  };

  const scopedRelationships = useMemo(() => {
    if (!selectedSeason || !selectedSpecies?.id) return [];
    return profile.relationships.filter((relationship) =>
      (relationship.scopes || []).some(
        (scope) =>
          scope.season === selectedSeason &&
          scope.species_id === selectedSpecies.id
      )
    );
  }, [profile.relationships, selectedSeason, selectedSpecies?.id]);

  const exporters = useMemo(() => {
    const map = new Map<number, string>();
    scopedRelationships.forEach((relationship) => {
      map.set(relationship.exporter_id, relationship.exporter_name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [scopedRelationships]);

  useEffect(() => {
    if (
      selectedExporter !== 'all' &&
      !exporters.some((exporter) => exporter.id === Number(selectedExporter))
    ) {
      setSelectedExporter('all');
    }
  }, [exporters, selectedExporter]);

  useEffect(() => {
    const load = async () => {
      if (!selectedSeason || !selectedSpecies?.id) {
        setRows([]);
        setShipmentRows([]);
        setErrorKey(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorKey(null);
      try {
        const filters = {
          season: selectedSeason,
          speciesId: selectedSpecies.id,
          exporterId:
            selectedExporter === 'all' ? null : Number(selectedExporter),
          exporterReceiverIds: null,
        };
        const [commitmentsData, shipments] = await Promise.all([
          getReceiverCommitments(filters),
          getReceiverShipmentsPage(filters),
        ]);
        setRows(commitmentsData.commitments);
        setNotes(commitmentsData.notes);
        setShipmentRows(shipments.rows);
      } catch (loadError) {
        console.error('Error loading receiver commitments:', loadError);
        setErrorKey('commitments.loadError');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedSeason, selectedSpecies?.id, selectedExporter]);

  const seasonWeeks = useMemo(
    () => getSeasonWeeksForSpecies(selectedSpecies?.id, selectedSeason),
    [selectedSpecies?.id, selectedSeason]
  );

  const cutoffIndex = useMemo(
    () =>
      getSeasonCutoffIndex({
        weeks: seasonWeeks,
        selectedSeason,
      }),
    [seasonWeeks, selectedSeason]
  );

  const inFulfillmentScope = (week: number | null | undefined) => {
    if (fulfillmentMode === 'season') return true;
    if (!week || cutoffIndex < 0) return false;
    const weekIndex = seasonWeeks.indexOf(Number(week));
    return weekIndex >= 0 && weekIndex <= cutoffIndex;
  };

  const scopedCommitments = useMemo(
    () => rows.filter((row) => inFulfillmentScope(row.week)),
    [rows, fulfillmentMode, cutoffIndex, seasonWeeks]
  );

  const scopedShipments = useMemo(
    () =>
      shipmentRows.filter((shipment) =>
        inFulfillmentScope(getShipmentWeek(shipment, shipDateMode))
      ),
    [shipmentRows, fulfillmentMode, cutoffIndex, seasonWeeks, shipDateMode]
  );

  // Cada exportadora define en su programa los kilos de una caja equivalente.
  const kgPerCeqByExporter = useMemo(
    () =>
      new Map(
        rows
          .filter((row) => Number(row.kg_per_ceq) > 0)
          .map((row) => [row.exporter_id, Number(row.kg_per_ceq)])
      ),
    [rows]
  );

  const toEquivalentBoxes = (shipment: ReceiverShipmentRow) => {
    const kg =
      (Number(shipment.box_weight) || 0) * (Number(shipment.quantity) || 0);
    const boxSize =
      kgPerCeqByExporter.get(shipment.exporter_id) ||
      selectedSpecies?.default_unit_price ||
      5;
    return kg / boxSize;
  };

  const summary = useMemo(() => {
    const exportersSet = new Set(rows.map((row) => row.exporter_id));
    const weeksSet = new Set(rows.map((row) => row.week));
    const totalCommitted = rows.reduce(
      (sum, row) => sum + (Number(row.quantity) || 0),
      0
    );

    return {
      totalCommitted,
      activeExporters: exportersSet.size,
      activeWeeks: weeksSet.size,
      averageWeekly:
        weeksSet.size > 0 ? totalCommitted / Math.max(weeksSet.size, 1) : 0,
    };
  }, [rows]);

  const byWeek = useMemo(() => {
    const map = new Map<number, { committed: number; shipped: number }>();

    scopedCommitments.forEach((row) => {
      const week = Number(row.week);
      const current = map.get(week) || { committed: 0, shipped: 0 };
      current.committed += Number(row.quantity) || 0;
      map.set(week, current);
    });

    scopedShipments.forEach((row) => {
      const week = getShipmentWeek(row, shipDateMode);
      if (!week) return;
      const current = map.get(week) || { committed: 0, shipped: 0 };
      current.shipped += toEquivalentBoxes(row);
      map.set(week, current);
    });

    const weeklyRows = sortSeasonWeekRows(
      Array.from(map, ([week, values]) => ({
        label: `${t('shared.week')} ${week}`,
        value: values.committed,
        committed: values.committed,
        shipped: values.shipped,
      })),
      seasonWeeks
    );

    if (weekValueMode === 'weekly') return weeklyRows;

    let committedTotal = 0;
    let shippedTotal = 0;
    return weeklyRows.map((row) => {
      committedTotal += row.committed || 0;
      shippedTotal += row.shipped || 0;
      return {
        ...row,
        value: committedTotal,
        committed: committedTotal,
        shipped: shippedTotal,
      };
    });
  }, [
    scopedCommitments,
    scopedShipments,
    seasonWeeks,
    shipDateMode,
    selectedSpecies?.default_unit_price,
    weekValueMode,
    t,
  ]);

  const byExporter = useMemo(() => {
    const map = new Map<
      number,
      { label: string; committed: number; shipped: number }
    >();

    scopedCommitments.forEach((row) => {
      const current = map.get(row.exporter_id) || {
        label: row.exporter_name,
        committed: 0,
        shipped: 0,
      };
      current.committed += Number(row.quantity) || 0;
      map.set(row.exporter_id, current);
    });

    scopedShipments.forEach((row) => {
      const current = map.get(row.exporter_id) || {
        label: row.exporter_name,
        committed: 0,
        shipped: 0,
      };
      current.shipped += toEquivalentBoxes(row);
      map.set(row.exporter_id, current);
    });

    return Array.from(map.values())
      .map((row) => ({
        label: row.label,
        value: row.committed,
        committed: row.committed,
        shipped: row.shipped,
      }))
      .sort(
        (a, b) =>
          (b.committed || 0) +
          (b.shipped || 0) -
          ((a.committed || 0) + (a.shipped || 0))
      )
      .slice(0, 8);
  }, [scopedCommitments, scopedShipments, selectedSpecies?.default_unit_price]);

  const detailMatrixRows = useMemo(() => {
    const map = new Map<
      number,
      {
        exporter_id: number;
        exporter_name: string;
        weeks: Record<number, number>;
        total: number;
      }
    >();

    rows.forEach((row) => {
      const current = map.get(row.exporter_id) || {
        exporter_id: row.exporter_id,
        exporter_name: row.exporter_name,
        weeks: {},
        total: 0,
      };
      const quantity = Number(row.quantity) || 0;
      current.weeks[row.week] = (current.weeks[row.week] || 0) + quantity;
      current.total += quantity;
      map.set(row.exporter_id, current);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.exporter_name.localeCompare(b.exporter_name)
    );
  }, [rows]);

  const handleExport = () => {
    const columns =
      detailViewMode === 'matrix'
        ? [
            t('shared.exporter'),
            ...seasonWeeks.map((week) => `${t('shared.week')} ${week}`),
            t('shared.total'),
          ]
        : [
            t('shared.exporter'),
            t('shared.client'),
            t('shared.season'),
            t('shared.species'),
            t('shared.week'),
            t('shared.quantity'),
          ];
    const exportRows =
      detailViewMode === 'matrix'
        ? detailMatrixRows.map((row) => [
            row.exporter_name,
            ...seasonWeeks.map((week) => row.weeks[week] || 0),
            row.total,
          ])
        : rows.map((commitment) => [
            commitment.exporter_name,
            commitment.client_name,
            commitment.season,
            selectedSpecies?.name || '',
            commitment.week,
            commitment.quantity,
          ]);

    const worksheet = XLSX.utils.aoa_to_sheet([columns, ...exportRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Commitments');
    XLSX.writeFile(workbook, `commitments-${selectedSeason || 'all'}.xlsx`);
  };

  const kpiCards = [
    {
      label: t('commitments.summary.totalCommitted'),
      value: formatNumber(summary.totalCommitted, locale),
      detail: t('shared.quantity'),
    },
    {
      label: t('commitments.summary.activeExporters'),
      value: formatNumber(summary.activeExporters, locale),
      detail: t('shared.exporter'),
    },
    {
      label: t('commitments.summary.averageWeekly'),
      value: formatNumber(summary.averageWeekly, locale),
      detail: `${formatNumber(summary.activeWeeks, locale)} ${t('shared.weeks')}`,
    },
  ];

  return (
    <PageLayout className='h-full overflow-auto'>
      <PageHeader
        title={view === 'summary'
            ? t('commitments.summary.title')
            : t('commitments.detail.title')}
        description={view === 'summary'
            ? t('commitments.summary.subtitle')
            : t('commitments.subtitle')}
      />

      <Card shadow='sm' className='mb-5'>
        <CardBody className='gap-4 p-4'>
          <div className='flex flex-col gap-1'>
            <p className='text-sm font-semibold text-foreground'>
              {t('shared.filters')}
            </p>
            <p className='text-xs text-default-500'>
              {t('commitments.filtersDescription')}
            </p>
          </div>

          <div className='flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4'>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-3 flex-1'>
              <Select
                label={t('shared.exporter')}
                selectedKeys={[selectedExporter]}
                onChange={(event) =>
                  setSelectedExporter(event.target.value || 'all')
                }
              >
                <SelectItem key='all'>{t('shared.allExporters')}</SelectItem>
                {exporters.map((exporter) => (
                  <SelectItem key={String(exporter.id)}>
                    {exporter.name}
                  </SelectItem>
                ))}
              </Select>
              {view === 'summary' ? (
                <>
                  <Select
                    label={t('commitments.summary.fulfillmentMode')}
                    selectedKeys={[fulfillmentMode]}
                    onChange={(event) =>
                      setFulfillmentMode(
                        (event.target.value || 'toDate') as FulfillmentMode
                      )
                    }
                  >
                    <SelectItem key='toDate'>
                      {t('commitments.summary.toDate')}
                    </SelectItem>
                    <SelectItem key='season'>
                      {t('commitments.summary.totalSeason')}
                    </SelectItem>
                  </Select>
                  <Select
                    label={t('commitments.summary.dateBase')}
                    selectedKeys={[shipDateMode]}
                    onChange={(event) =>
                      setShipDateMode(
                        (event.target.value || 'etd') as ShipDateMode
                      )
                    }
                  >
                    <SelectItem key='etd'>
                      {t('commitments.summary.etd')}
                    </SelectItem>
                    <SelectItem key='eta'>
                      {t('commitments.summary.eta')}
                    </SelectItem>
                  </Select>
                </>
              ) : null}
            </div>

            {view === 'detail' ? (
              <div className='flex flex-col sm:flex-row sm:items-end gap-4'>
                <div className='flex flex-col gap-1'>
                  <span className='text-xs font-medium text-default-500'>
                    {t('commitments.detail.viewMode')}
                  </span>
                  <Tabs
                    size='sm'
                    aria-label={t('commitments.detail.viewMode')}
                    selectedKey={detailViewMode}
                    onSelectionChange={(key) =>
                      setDetailViewMode(key as DetailViewMode)
                    }
                  >
                    <Tab key='list' title={t('commitments.detail.listView')} />
                    <Tab
                      key='matrix'
                      title={t('commitments.detail.matrixView')}
                    />
                  </Tabs>
                </div>
                <div className='hidden h-10 w-px bg-default-200 sm:block' />
                <div className='flex flex-col gap-1'>
                  <span className='text-xs font-medium text-default-500'>
                    {t('shared.actions')}
                  </span>
                  <Button
                    variant='flat'
                    onPress={handleExport}
                    isDisabled={loading}
                  >
                    {t('shared.exportExcel')}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>

      {loading ? (
        <LoadingState />
      ) : errorKey ? (
        <Alert variant='danger' showIcon>
          {t(errorKey)}
        </Alert>
      ) : view === 'summary' ? (
        <div className='space-y-5'>
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            {kpiCards.map((card) => (
              <KpiCard
                key={card.label}
                label={card.label}
                value={card.value}
                detail={card.detail}
              />
            ))}
          </div>

          <div className='grid grid-cols-1 xl:grid-cols-2 gap-5'>
            <SummaryChartCard
              title={t('commitments.summary.byWeek')}
              rows={byWeek}
              locale={locale}
              mode={chartModes.week}
              onModeChange={(mode) => setChartMode('week', mode)}
              chartLabel={t('shared.chart')}
              tableLabel={t('shared.table')}
              weeklyLabel={t('commitments.summary.weekly')}
              cumulativeLabel={t('commitments.summary.cumulative')}
              weekValueMode={weekValueMode}
              onWeekValueModeChange={setWeekValueMode}
              nameLabel={t('shared.name')}
              committedLabel={t('shared.committed')}
              shippedLabel={t('shared.shipped')}
              emptyLabel={t('commitments.empty')}
            />
            <SummaryChartCard
              title={t('commitments.summary.byExporter')}
              rows={byExporter}
              locale={locale}
              mode={chartModes.exporter}
              onModeChange={(mode) => setChartMode('exporter', mode)}
              chartLabel={t('shared.chart')}
              tableLabel={t('shared.table')}
              nameLabel={t('shared.name')}
              committedLabel={t('shared.committed')}
              shippedLabel={t('shared.shipped')}
              emptyLabel={t('commitments.empty')}
            />
          </div>
        </div>
      ) : detailViewMode === 'matrix' ? (
        <div className='overflow-x-auto'>
          <Table aria-label={t('commitments.detail.matrixView')}>
            <TableHeader>
              <TableColumn>{t('shared.exporter')}</TableColumn>
              {seasonWeeks.map((week) => (
                <TableColumn
                  key={`week-${week}`}
                >{`${t('shared.week')} ${week}`}</TableColumn>
              ))}
              <TableColumn>{t('shared.total')}</TableColumn>
            </TableHeader>
            <TableBody
              emptyContent={t('commitments.empty')}
              items={detailMatrixRows}
            >
              {(row) => (
                <TableRow key={row.exporter_id}>
                  <TableCell>{row.exporter_name}</TableCell>
                  {seasonWeeks.map((week) => {
                    const weekNotes = notes.filter(
                      (note) => note.exporter_id === row.exporter_id && note.week === week
                    );
                    return (
                      <TableCell
                        key={`${row.exporter_id}-${week}`}
                        title={weekNotes.map((note) => note.content).join('\n') || undefined}
                      >
                        <span className='relative'>
                          {formatNumber(row.weeks[week] || 0, locale)}
                          {weekNotes.length > 0 && (
                            <span className='absolute -right-2 -top-1 h-1.5 w-1.5 rounded-full bg-primary' />
                          )}
                        </span>
                      </TableCell>
                    );
                  })}
                  <TableCell className='font-semibold'>
                    {formatNumber(row.total, locale)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <Table aria-label={t('commitments.ariaLabel')}>
          <TableHeader>
            <TableColumn>{t('shared.exporter')}</TableColumn>
            <TableColumn>{t('shared.client')}</TableColumn>
            <TableColumn>{t('shared.season')}</TableColumn>
            <TableColumn>{t('shared.week')}</TableColumn>
            <TableColumn>{t('shared.quantity')}</TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('commitments.empty')} items={rows}>
            {(commitment) => (
              <TableRow key={commitment.id}>
                <TableCell>{commitment.exporter_name}</TableCell>
                <TableCell>{commitment.client_name}</TableCell>
                <TableCell>{commitment.season}</TableCell>
                <TableCell>{commitment.week}</TableCell>
                <TableCell>
                  {formatNumber(Number(commitment.quantity) || 0, locale)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      {view === 'detail' && notes.length > 0 && (
        <Card shadow='sm' className='mt-5'>
          <CardBody className='gap-3 p-4'>
            <p className='font-semibold'>{t('commitments.notes.title')}</p>
            {notes.map((note) => (
              <div key={note.id} className='rounded-lg bg-default-100 p-3 text-sm'>
                <p className='mb-1 text-xs text-default-500'>
                  {note.exporter_name} ·{' '}
                  {note.week == null
                    ? t('commitments.notes.general')
                    : `${t('shared.week')} ${note.week}`}
                </p>
                <p className='whitespace-pre-wrap'>{note.content}</p>
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </PageLayout>
  );
};

export default ReceiverCommitmentsPage;
